import {
  Asset,
  AssetDetail,
  AssetAccount,
  AssetActivation,
  AssetPost,
  AssetCategory,
  AssetResponse,
  AssetsResponse,
  AssetAccountsResponse,
  AssetActivationsResponse,
  AssetPostsResponse,
  AssetPerformanceResponse,
  AssetPerformanceMetrics,
  AssetCategoriesResponse,
  CreateAssetRequest,
  UpdateAssetRequest,
  AssetStatus,
  AssetPagination
} from '../types/asset'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class AssetService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all assets with optional filtering and pagination
   * @param customerId - Customer ID (required)
   * @param status - Optional filter by status
   * @param categoryId - Optional filter by category ID
   * @param postTypeId - Optional filter by post type ID
   * @param search - Optional search query (searches asset name and description)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   * @param sortBy - Column to sort by (name, account_count, hashtag_count, total_emv)
   * @param sortOrder - Sort direction (asc, desc)
   */
  async getAssets(
    customerId: string,
    status?: AssetStatus,
    categoryId?: string,
    postTypeId?: string,
    search?: string,
    page?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<{ data: Asset[]; pagination: AssetPagination }> {
    const url = new URL(`${this.baseURL}/assets`)

    url.searchParams.append('customerId', customerId)

    if (status) {
      url.searchParams.append('status', status)
    }
    if (categoryId) {
      url.searchParams.append('categoryId', categoryId)
    }
    if (postTypeId) {
      url.searchParams.append('postTypeId', postTypeId)
    }
    if (search !== undefined && search.trim()) {
      url.searchParams.append('search', search.trim())
    }
    if (page !== undefined) {
      url.searchParams.append('page', String(page))
    }
    if (limit !== undefined) {
      url.searchParams.append('limit', String(limit))
    }
    if (sortBy) {
      url.searchParams.append('sortBy', sortBy)
    }
    if (sortOrder) {
      url.searchParams.append('sortOrder', sortOrder)
    }

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<AssetsResponse>(response)

    return {
      data: responseData.data,
      pagination: responseData.pagination
    }
  }

  /**
   * Get a single asset by ID
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   */
  async getAsset(id: string, customerId: string): Promise<AssetDetail> {
    const url = new URL(`${this.baseURL}/assets/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as AssetDetail
  }

  /**
   * Create a new asset
   * @param customerId - Customer ID (required)
   * @param assetData - Asset data
   */
  async createAsset(customerId: string, assetData: CreateAssetRequest): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(assetData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Update an existing asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param updates - Partial asset data to update
   */
  async updateAsset(
    id: string,
    customerId: string,
    updates: UpdateAssetRequest
  ): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PATCH',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Delete an asset (soft delete)
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   */
  async deleteAsset(id: string, customerId: string): Promise<void> {
    const url = new URL(`${this.baseURL}/assets/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }

  /**
   * Set (replace all) hashtags for an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param hashtagIds - Array of hashtag IDs (replaces all existing)
   */
  async setAssetHashtags(
    id: string,
    customerId: string,
    hashtagIds: number[]
  ): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets/${id}/hashtags`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ hashtagIds })
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Remove a hashtag from an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param hashtagId - Hashtag ID to remove
   */
  async removeAssetHashtag(
    id: string,
    customerId: string,
    hashtagId: number
  ): Promise<void> {
    const url = new URL(`${this.baseURL}/assets/${id}/hashtags/${hashtagId}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }

  // ========== Account Management Methods ==========

  /**
   * Get accounts linked to an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   */
  async getAssetAccounts(id: string, customerId: string): Promise<AssetAccount[]> {
    const url = new URL(`${this.baseURL}/assets/${id}/accounts`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetAccountsResponse>(response)

    return data.data
  }

  /**
   * Set (replace all) accounts for an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param accountIds - Array of account UUIDs (replaces all existing)
   */
  async setAssetAccounts(
    id: string,
    customerId: string,
    accountIds: string[]
  ): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets/${id}/accounts`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ accountIds })
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Add accounts to an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param accountIds - Array of account UUIDs to add
   */
  async addAssetAccounts(
    id: string,
    customerId: string,
    accountIds: string[]
  ): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets/${id}/accounts`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ accountIds })
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Remove an account from an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param accountId - Account UUID to remove
   */
  async removeAssetAccount(
    id: string,
    customerId: string,
    accountId: string
  ): Promise<void> {
    const url = new URL(`${this.baseURL}/assets/${id}/accounts/${accountId}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }

  // ========== Category Management Methods ==========

  /**
   * Get categories linked to an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   */
  async getAssetCategories(id: string, customerId: string): Promise<AssetCategory[]> {
    const url = new URL(`${this.baseURL}/assets/${id}/categories`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetCategoriesResponse>(response)

    return data.data
  }

  /**
   * Set (replace all) categories for an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param categoryIds - Array of category UUIDs (replaces all existing)
   */
  async setAssetCategories(
    id: string,
    customerId: string,
    categoryIds: string[]
  ): Promise<Asset> {
    const url = new URL(`${this.baseURL}/assets/${id}/categories`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ categoryIds })
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetResponse>(response)

    return data.data as Asset
  }

  /**
   * Remove a category from an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param categoryId - Category UUID to remove
   */
  async removeAssetCategory(
    id: string,
    customerId: string,
    categoryId: string
  ): Promise<void> {
    const url = new URL(`${this.baseURL}/assets/${id}/categories/${categoryId}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }

  // ========== Post Management Methods ==========

  /**
   * Get posts linked to an asset via hashtags
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   */
  async getAssetPosts(
    id: string,
    customerId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{ data: AssetPost[]; pagination: AssetPagination }> {
    const url = new URL(`${this.baseURL}/assets/${id}/posts`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('page', String(page))
    url.searchParams.append('limit', String(limit))

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<AssetPostsResponse>(response)

    return {
      data: responseData.data,
      pagination: responseData.pagination
    }
  }

  // ========== Activation Management Methods ==========

  /**
   * Get activations linked to an asset via hashtags
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   */
  async getAssetActivations(id: string, customerId: string): Promise<AssetActivation[]> {
    const url = new URL(`${this.baseURL}/assets/${id}/activations`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetActivationsResponse>(response)

    return data.data
  }

  /**
   * Get performance metrics for an asset
   * @param id - Asset UUID
   * @param customerId - Customer ID (required)
   * @param startDate - Optional start date (YYYY-MM-DD)
   * @param endDate - Optional end date (YYYY-MM-DD)
   */
  async getAssetPerformance(
    id: string,
    customerId: string,
    startDate?: string,
    endDate?: string
  ): Promise<AssetPerformanceMetrics> {
    const url = new URL(`${this.baseURL}/assets/${id}/performance`)
    url.searchParams.append('customerId', customerId)

    if (startDate) {
      url.searchParams.append('startDate', startDate)
    }
    if (endDate) {
      url.searchParams.append('endDate', endDate)
    }

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetPerformanceResponse>(response)

    return data.data
  }

  /**
   * Export asset performance data to CSV
   * @param customerId - Customer ID (required)
   * @param assetIds - Optional array of asset UUIDs (exports all active assets if omitted)
   * @param startDate - Optional start date (YYYY-MM-DD)
   * @param endDate - Optional end date (YYYY-MM-DD)
   * @returns Blob containing CSV data
   */
  async exportAssets(
    customerId: string,
    assetIds?: string[],
    startDate?: string,
    endDate?: string
  ): Promise<Blob> {
    const url = new URL(`${this.baseURL}/assets/export`)
    url.searchParams.append('customerId', customerId)

    if (assetIds && assetIds.length > 0) {
      url.searchParams.append('assetIds', assetIds.join(','))
    }
    if (startDate) {
      url.searchParams.append('startDate', startDate)
    }
    if (endDate) {
      url.searchParams.append('endDate', endDate)
    }

    const timeoutMs = 60000 // Longer timeout for export operations

    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: this.getAuthToken() ? `Bearer ${this.getAuthToken()}` : ''
      },
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }

    return response.blob()
  }

  /**
   * Get asset performance within an activation context
   * Filters by: asset's hashtags + asset's accounts + activation's date range
   * @param assetId - Asset UUID
   * @param activationId - Activation UUID
   * @param customerId - Customer ID (required)
   */
  async getAssetActivationPerformance(
    assetId: string,
    activationId: string,
    customerId: string
  ): Promise<AssetPerformanceMetrics> {
    const url = new URL(`${this.baseURL}/assets/${assetId}/activations/${activationId}/performance`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<AssetPerformanceResponse>(response)

    return data.data
  }
}

export const assetService = new AssetService()
